import { useEffect, useMemo, useState } from 'react';
import { Search } from '@mui/icons-material';
import { Box, capitalize } from '@mui/material';
import { common } from '@mui/material/colors';
import dayjs from 'dayjs';
import { get } from 'lodash';
import { observer } from 'mobx-react-lite';
import { FuseSelect, ListPage, ListRow } from '/src/components';
import { useAssetStore, useFilterStore, useUserStore } from '/src/context';
import { mediumDate } from '/src/lib/constants';
import { Note } from '/src/lib/models';
import { AssetStatus, DataModelType } from '/src/lib/types';
import * as utils from '/src/utils';

/**
 * List Assets Page
 */
export const ListAssetsPage = observer(() => {
  const defaultSortType = 'effectiveDate';
  const defaultSortReverse = true;

  /** Context **/
  const assetStore = useAssetStore();
  const filterStore = useFilterStore();
  const userStore = useUserStore();

  /** State **/
  const [search, setSearch] = useState<Record<string, string>>({
    status: AssetStatus.Open,
  });
  const [sortType, setSortType] = useState(defaultSortType);
  const [sortReverse, setSortReverse] = useState(defaultSortReverse);
  const [notes, setNotes] = useState<Note[]>();
  const [notesName, setNotesName] = useState<string>();

  /** Computed **/
  const assets = assetStore.assets;
  const isFiltered = !!Object.values(search).length;

  const companies = useMemo(() => filterStore.companyListGet(), [filterStore._companyList]);

  const listData = useMemo(() => {
    if (assets) {
      let list = assets.toSorted((a, b) => (get(a, sortType) > get(b, sortType) ? 1 : -1));
      if (sortReverse) list.reverse();

      if (search.companyId) list = list.filter((asset) => asset.company?._id === search.companyId);
      if (search.status) list = list.filter((asset) => asset.status === search.status);

      return list;
    }
  }, [assets, search, sortType, sortReverse]);

  /** Methods **/

  const sortList = (type?: string) => {
    if (type) {
      if (sortType === type) setSortReverse(!sortReverse);
      else setSortType(type);
    }
  };

  const showNotesPopup = (notes?: Note[], name?: string) => {
    setNotes(notes ?? []);
    setNotesName(name);
  };

  /** Effects **/

  useEffect(() => {
    assetStore.query();
  }, []);

  /** Render **/
  return (
    <ListPage
      data={listData}
      modelType={DataModelType.Asset}
      headerItems={[
        { flex: 5, propName: 'notes', label: 'Notes' },
        { propName: 'name', label: 'Asset Name' },
        { propName: 'loan.name', label: 'Deal' },
        { propName: 'program', label: 'Program' },
        { propName: 'value', label: 'Value', textAlign: 'right' },
        { propName: 'status', label: 'Status', textAlign: 'right' },
        { propName: 'totalAdjustments', label: 'Total Adjustments', textAlign: 'right' },
        { propName: 'totalPayments', label: 'Total Payments', textAlign: 'right' },
        { propName: 'effectiveDate', label: 'Asset Effective Date', textAlign: 'right' },
        { propName: 'nextDate', label: 'Next Payment', textAlign: 'right' },
      ]}
      toolbarItems={
        <>
          {userStore.isAdmin && (
            <Box display="flex" flex={1}>
              <Box display="flex" justifyContent="start" alignItems="center" gap="3px">
                <Search />
                <FuseSelect
                  value={search.companyId}
                  updateValue={(id) => setSearch({ ...search, companyId: id })}
                  options={companies
                    .filter((company) => !company.isLender)
                    .map((company) => ({ label: company.name, value: company._id }))}
                  placeholder="All Companies"
                  enablePlaceholderOption
                  boxProps={{ className: 'no-margin-top-bottom' }}
                  tooltip="Filter by Company"
                  disableUnderline
                  disableMargin
                  color={common.white}
                />
              </Box>
            </Box>
          )}
          <Box display="flex" flex={1}>
            <Box display="flex" justifyContent="start" alignItems="center" gap="3px">
              <Search />
              <FuseSelect
                value={search.status}
                updateValue={(status) => setSearch({ ...search, status })}
                options={Object.entries(AssetStatus).map(([label, value]) => ({ label, value }))}
                placeholder="Select Status"
                tooltip="Filter by Status"
                disableUnderline
                disableMargin
                color={common.white}
              />
            </Box>
          </Box>
        </>
      }
      isFiltered={isFiltered}
      sortType={sortType}
      sortReverse={sortReverse}
      sortList={sortList}
      notes={notes}
      notesName={notesName}
      setNotes={setNotes}
      setNotesName={setNotesName}
    >
      {listData?.map((asset, i) => (
        <ListRow
          key={asset._id}
          items={[
            {
              propName: 'notes',
              isNotes: true,
              flex: 5,
              hide: !asset.notes?.length,
              showNotesPopup: () => showNotesPopup(asset.notes, asset.name),
            },
            { propName: 'name', content: asset.name, url: `/assets/${asset._id}` },
            { propName: 'loan.name', content: asset.loan.name, url: `/deals/${asset.loan._id}` },
            { propName: 'program', content: asset.program },
            { propName: 'value', content: utils.currency.format(asset.value), textAlign: 'right' },
            { propName: 'status', content: capitalize(asset.status), textAlign: 'right' },
            {
              propName: 'totalAdjustments',
              content: utils.currency.format(asset.totalAdjustments ?? 0),
              textAlign: 'right',
            },
            {
              propName: 'totalPayments',
              content: utils.currency.format(asset.totalPayments ?? 0),
              textAlign: 'right',
            },
            {
              propName: 'effectiveDate',
              content: dayjs(asset.effectiveDate).format(mediumDate),
              textAlign: 'right',
            },
            { propName: 'nextDate', content: dayjs(asset.nextDate).format(mediumDate), textAlign: 'right' },
          ]}
          rowIndex={i}
        />
      ))}
    </ListPage>
  );
});
